// Client-side recovery from stale dynamic-import (chunk) load failures.
//
// When we deploy npt-web, the hashed bundle filenames change. A tab that
// was loaded before the deploy still references the OLD hashes; the moment
// it lazy-loads a route/component chunk (React.lazy / dynamic import) it
// asks for a filename the new build no longer serves. Vite's runtime emits
// a `vite:preloadError` event on window when that fetch fails.
//
// The right response is simply to reload: a fresh document pulls the new
// index.html and the current chunk graph, and the user lands where they
// were. This is the client-side complement to the two server/infra fixes
// in NPT-1358 (the /assets/* 404 guard in src/server/index.ts and moving
// /assets/* to S3) — it covers the one case those can't: a tab left open
// across a deploy that navigates to a chunk that is genuinely gone.
//
// Guard against reload loops. If the import keeps failing for a reason that
// a reload won't fix (a real network outage, an offline device), we must
// not spin. We record the last reload time in sessionStorage and skip the
// reload if we already reloaded within RELOAD_DEBOUNCE_MS — but we DON'T
// latch forever, so a genuine second deploy later in the same tab session
// still recovers.
const RELOAD_STORAGE_KEY = 'npt:chunkReloadAt';
const RELOAD_DEBOUNCE_MS = 10_000;

export function registerChunkReloadHandler(): void {
   if (typeof window === 'undefined') return;

   window.addEventListener('vite:preloadError', event => {
      // Stop Vite from rethrowing the error (which would surface as an
      // unhandled rejection in Sentry) — we're handling it by reloading.
      event.preventDefault();

      let lastReloadAt = 0;
      try {
         lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_STORAGE_KEY)) || 0;
      } catch {
         // sessionStorage can throw (private mode / disabled storage). Treat
         // as "never reloaded" and fall through to a single reload attempt.
      }

      if (Date.now() - lastReloadAt < RELOAD_DEBOUNCE_MS) {
         // Already reloaded moments ago and it still failed — a reload won't
         // help right now. Let the failure stand rather than loop.
         return;
      }

      try {
         window.sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
      } catch {
         // Ignore — worst case we lose loop protection, but a persistent
         // failure is far rarer than the deploy-skew case we're fixing.
      }

      window.location.reload();
   });
}
